import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { SubscripcionService } from '@core/services/subscripcion.service';
import { ModalController } from '@ionic/angular';
import { ToastService } from '@shared/events/toast.service';
import { LoadingService } from '@shared/loading';
import { DatepickerEvent } from '@shared/models/datepicker-event';
import { Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { CropReciboService } from './crop-recibo/crop-recibo.service';
import { Bancos } from '@pages/cuenta/suscripcion/models/facturacion.interface';

@Component({
  selector: 'app-cargar-recibo',
  templateUrl: './cargar-recibo.component.html',
  styleUrls: ['./cargar-recibo.component.scss'],
})
export class CargarReciboComponent implements OnInit, OnDestroy {
  @Input() canClose: boolean;
  @Input() type: number;
  @Input() nombreOrg: string;

  bancos: Bancos[];
  imageError = false;
  reciboForm: FormGroup;

  private _unsubscribeAll = new Subject<void>();

  constructor(
    private _cropService: CropReciboService,
    private _formBuilder: FormBuilder,
    private _loading: LoadingService,
    private _modalCtrl: ModalController,
    private _router: Router,
    private _subscriptionService: SubscripcionService,
    private _toastService: ToastService,
  ) {}

  ngOnInit() {
    this._subscriptionService.getBancos().subscribe(resp => {
      this.bancos = resp;
    });

    this.buildForm();
    this._cropService.imagen$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(() => {
        if (this._cropService.recibo) {
          this.reciboForm
            .get('ImagenRecibo')
            .setValue(this._cropService.recibo);
        } else {
          this.reciboForm.get('ImagenRecibo').reset(null);
        }
      });

    this.reciboForm
      .get('Banco')
      .valueChanges.pipe(takeUntil(this._unsubscribeAll))
      .subscribe((id) => this.changeBanco(id));
  }

  ngOnDestroy(): void {
    this._unsubscribeAll.next();
    this._unsubscribeAll.complete();
  }

  private buildForm() {
    this.reciboForm = this._formBuilder.group({
      CodOrganizacion: [+localStorage.getItem('CodOrganizacion')],
      CodUsuario: [+localStorage.getItem('CodUsuario')],
      Banco: ['', [Validators.required]],
      FechaPago: [new Date()],
      ImagenRecibo: ['', [Validators.required]],
      NumReferencia: [null, [Validators.required]],
      TipoCuenta: ['', [Validators.required]],
      CuentaDolares: ['000000000000000'],
      CuentaLempiras: ['000000000000000'],
      NombreOrganizacion: [this.nombreOrg],
      ValidacionManual: [false],
    });
  }

  changeBanco(id: string) {
    if (id) {
      this.reciboForm
        .get('CuentaDolares')
        .setValue(this.bancos.find((x) => x.Id === id).CuentaDolares);
      this.reciboForm
        .get('CuentaLempiras')
        .setValue(this.bancos.find((x) => x.Id === id).CuentaLempiras);
    }
  }

  set account(value: string) {
    this.reciboForm.get('TipoCuenta').setValue(value);
  }

  onChangeDate(event: DatepickerEvent) {
    this.reciboForm.get('FechaPago').setValue(event.detail.date);
  }

  goToMethods() {
    this._router.navigate(['/cuenta/suscripcion/metodos']);
    this.dismiss();
  }

  downloadInstructions() {
    const downloadLink = document.createElement('a');
    downloadLink.href = 'assets/utils/Instrucciones de pago - Yalo Cobro.png';
    downloadLink.download = 'Instrucciones de pago - Yalo Cobro';
    downloadLink.click();
  }

  sendData() {
    this.imageError = false;
    this._toastService.toastInfo(
      'Este proceso puede demorar más tiempo de lo normal, por favor espera hasta que termine'
    );
    this._loading.isLoading = true;
    if (this.type === 1) {
      this._cropService
        .createRecibo(this.reciboForm.value)
        .pipe(finalize(() => (this._loading.isLoading = false)))
        .subscribe(
          () => {
            this.successToast();
            this.dismiss();
            this._subscriptionService.hasActiveSubscription = true;
            this._router.navigate(['/dashboard']);
          },
          (err) => {
            if (err.error) {
              this.manageErrors(err.error);
            }
          }
        );
    } else if (this.type === 2) {
      this._cropService
        .validateRecibo(this.reciboForm.value)
        .pipe(finalize(() => (this._loading.isLoading = false)))
        .subscribe(
          (imagen) => {
            this.successToast();
            this.reciboForm.get('ImagenRecibo').setValue(imagen);
            this._modalCtrl.dismiss({
              receiptUploaded: true,
              recibo: this.reciboForm.value,
            });
            this._cropService.reset();
          },
          (err) => {
            if (err.error) {
              this.manageErrors(err.error);
            }
          }
        );
    } else {
      this._cropService
        .createReciboSU(this.reciboForm.value)
        .pipe(finalize(() => (this._loading.isLoading = false)))
        .subscribe(
          () => {
            this.successToast();
            this._modalCtrl.dismiss({
              receiptUploaded: true,
              recibo: this.reciboForm.value,
            });
            this._cropService.reset();
          },
          (err) => {
            if (err.error) {
              this.manageErrors(err.error);
            }
          }
        );
    }
  }

  manageErrors(data: string) {
    const errors = data;
    if (errors.startsWith('ERROR')) {
      const splitted = data.split(',');
      this._toastService.toastError(splitted[1]);
    } else {
      const splitted = data.split(',');
      // if (splitted.includes('Banco')) {
      //   this.reciboForm.get('Banco').setErrors({ incorrect: true });
      // }
      if (splitted.includes('Numero de referencia')) {
        this.reciboForm.get('NumReferencia').setErrors({ incorrect: true });
      }
      if (splitted.includes('Banco')) {
        this._toastService.toastError("Banco incorrecto o no se encuentra en el recibo.");
      }
      if (splitted.includes('Imagen')) {
        this.imageError = true;
        this._toastService.toastError("Recibo no válido, por favor intenta de nuevo o contacta con soporte.");
        this._cropService.reset();
      }
    }
  }

  successToast() {
    this._toastService.toastSuccessful(
      'Estamos revisando la información de pago, recibirás un correo electrónico cuando sea validado. Este proceso suele demorar 1 día hábil.'
    );
  }

  dismiss() {
    this._modalCtrl.dismiss();
    this._cropService.reset();
  }
}
